// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/compras/services/recebimentoService.ts
//  Serviço do fluxo de Recebimento com XML (Fase 2, Iteração 1).
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  AgrupamentoFluxoCaixa,
  AjustePrecoPreview,
  AplicarAjustePrecoRequest,
  AplicarAjustePrecoResultado,
  BaixarXmlSefazResultado,
  ConfirmarRecebimentoRequest,
  FluxoCaixaComprasResponse,
  ManifestacaoHistoricoItem,
  ManifestacaoResultado,
  ManifestarDfeRequest,
  PaginadoResponse,
  FiltrosListaCompras,
  RecebimentoConfirmado,
  RecebimentoDetalhe,
  RecebimentoListagem,
  RecebimentoPreview,
  RecebimentoStaging,
  PerformanceFornecedoresResponse,
  RelatorioPosResponse,
  SpendAnalyticsResponse,
} from '../types';
import { paramsListaCompras } from '../types';

const BASE = '/api/compras/recebimentos';

/** Filtros da consulta de recebimentos: os comuns das listas (busca, período do recebimento, ordenação) + fornecedor/status. */
export interface FiltrosRecebimento extends FiltrosListaCompras {
  fornecedorId?: number;
  status?: string;
}

export const recebimentoService = {
  /**
   * Faz upload do XML de NF-e e retorna o staging parseado (não persistido).
   * Iteração 1 — apenas parsing e validação estática.
   */
  importarXml: (arquivo: File, cnpjDestinatario?: string) => {
    const form = new FormData();
    form.append('arquivo', arquivo);

    return apiClient
      .post<RecebimentoStaging>(`${BASE}/importar-xml`, form, {
        params: cnpjDestinatario ? { cnpjDestinatario } : undefined,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  /**
   * Upload + matching contra banco (fornecedor/produto/PO) → RecebimentoPreview.
   * Iteração 2 — ainda não persiste.
   */
  preview: (
    arquivo: File,
    opts?: { cnpjDestinatario?: string; compraOrdemId?: number },
  ) => {
    const form = new FormData();
    form.append('arquivo', arquivo);

    const params: Record<string, string | number> = {};
    if (opts?.cnpjDestinatario) params.cnpjDestinatario = opts.cnpjDestinatario;
    if (opts?.compraOrdemId)    params.compraOrdemId    = opts.compraOrdemId;

    return apiClient
      .post<RecebimentoPreview>(`${BASE}/preview`, form, {
        params: Object.keys(params).length > 0 ? params : undefined,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  /**
   * Confirma o recebimento (persiste). Retorna o resumo do que foi criado.
   */
  confirmar: (request: ConfirmarRecebimentoRequest) =>
    apiClient.post<RecebimentoConfirmado>(BASE, request).then((r) => r.data),

  /**
   * Preview do ajuste de preço de venda pós-recebimento.
   * Calcula custo de reposição + preço sugerido por item.
   */
  ajustePrecoPreview: (recebimentoId: number, empresaId?: number) =>
    apiClient
      .get<AjustePrecoPreview>(`${BASE}/${recebimentoId}/ajuste-preco`, {
        params: empresaId ? { empresaId } : undefined,
      })
      .then((r) => r.data),

  /**
   * Aplica os preços escolhidos e grava log de auditoria.
   */
  ajustePrecoAplicar: (recebimentoId: number, request: AplicarAjustePrecoRequest) =>
    apiClient
      .post<AplicarAjustePrecoResultado>(`${BASE}/${recebimentoId}/ajuste-preco`, request)
      .then((r) => r.data),

  /**
   * Envia manifestação DFe à SEFAZ (Ciência/Confirmação/Desconhecimento/Não-Realizada).
   */
  manifestar: (
    recebimentoId: number,
    request: ManifestarDfeRequest,
    empresaId?: number,
  ) =>
    apiClient
      .post<ManifestacaoResultado>(`${BASE}/${recebimentoId}/manifestacao`, request, {
        params: empresaId ? { empresaId } : undefined,
      })
      .then((r) => r.data),

  /**
   * Histórico de manifestações deste recebimento (pela chave NF-e).
   */
  listarManifestacoes: (recebimentoId: number) =>
    apiClient
      .get<ManifestacaoHistoricoItem[]>(`${BASE}/${recebimentoId}/manifestacoes`)
      .then((r) => r.data),

  /**
   * Lista paginada de recebimentos.
   */
  listar: (pagina = 1, tamanhoPagina = 20, filtros: FiltrosRecebimento = {}) =>
    apiClient
      .get<PaginadoResponse<RecebimentoListagem>>(BASE, {
        params: {
          pagina, tamanhoPagina, ...paramsListaCompras(filtros),
          fornecedorId: filtros.fornecedorId || undefined,
          status: filtros.status || undefined,
        },
      })
      .then((r) => r.data),

  /**
   * Detalhe de um recebimento com itens.
   */
  obter: (id: number) =>
    apiClient.get<RecebimentoDetalhe>(`${BASE}/${id}`).then((r) => r.data),

  /**
   * (Stub) Tenta baixar XML da NF-e direto da SEFAZ pela chave.
   */
  baixarXmlSefaz: (chaveNfe: string) =>
    apiClient
      .post<BaixarXmlSefazResultado>(`${BASE}/baixar-xml-sefaz`, { chaveNfe })
      .then((r) => r.data),
};

// ── Relatórios ───────────────────────────────────────────────────────────────

const RELATORIOS = '/api/compras/relatorios';

export const relatoriosComprasService = {
  fluxoCaixa: (params: {
    dataInicio?: string;
    dataFim?: string;
    fornecedorId?: number;
    agrupamento?: AgrupamentoFluxoCaixa;
    apenasAbertos?: boolean;
  }) =>
    apiClient
      .get<FluxoCaixaComprasResponse>(`${RELATORIOS}/fluxo-caixa`, { params })
      .then((r) => r.data),

  spendAnalytics: (params: {
    dataInicio?: string;
    dataFim?: string;
    fornecedorId?: number;
    grupoProdutoId?: number;
  }) =>
    apiClient
      .get<SpendAnalyticsResponse>(`${RELATORIOS}/spend-analytics`, { params })
      .then((r) => r.data),

  performanceFornecedores: (params: {
    dataInicio?: string;
    dataFim?: string;
    fornecedorId?: number;
  }) =>
    apiClient
      .get<PerformanceFornecedoresResponse>(`${RELATORIOS}/performance-fornecedores`, { params })
      .then((r) => r.data),

  pedidos: (params: {
    dataInicio?: string;
    dataFim?: string;
    fornecedorId?: number;
    status?: string;
  }) =>
    apiClient
      .get<RelatorioPosResponse>(`${RELATORIOS}/pedidos`, { params })
      .then((r) => r.data),
};
